/**
 * Upload service types and interfaces.
 * Defines structures for JSON, CSV, and form-based data uploads.
 */
import { z } from "zod";
// ============================================================================
// Upload Format Types
// ============================================================================
/** Supported upload formats */
export const UploadFormatSchema = z.enum(["json", "csv", "form"]);
/** Upload job status */
export const UploadJobStatusSchema = z.enum([
    "pending",
    "parsing",
    "validating",
    "previewing",
    "committing",
    "completed",
    "failed",
]);
// ============================================================================
// CSV Specific Types
// ============================================================================
/** CSV delimiter options */
export const CsvDelimiterSchema = z.enum([",", ";", "\t", "|"]);
/** Transform function to apply to CSV values */
export const TransformTypeSchema = z.enum([
    "none",
    "trim",
    "lowercase",
    "uppercase",
    "number",
    "boolean",
    "date",
]);
/** Column mapping from CSV to product attribute */
export const ColumnMappingSchema = z
    .object({
    /** CSV column identifier (name or 0-based index) */
    csvColumn: z.union([z.string().min(1), z.number().int().min(0)]),
    /** Target attribute key in the product schema */
    attributeKey: z.string().min(1),
    /** Transform to apply to the value */
    transform: TransformTypeSchema.optional().default("none"),
    /** Default value if the column is empty */
    defaultValue: z.unknown().optional(),
})
    .strict();
/** CSV parsing options */
export const CsvOptionsSchema = z
    .object({
    /** Whether the CSV has a header row */
    hasHeader: z.boolean().default(true),
    /** Field delimiter */
    delimiter: CsvDelimiterSchema.default(","),
    /** Number of rows to skip from the beginning (before header if hasHeader is true) */
    skipRows: z.number().int().min(0).default(0),
    /** Quote character for escaping */
    quoteChar: z.string().length(1).default('"'),
    /** Escape character */
    escapeChar: z.string().length(1).default('"'),
    /** Trim whitespace from values */
    trimValues: z.boolean().default(true),
    /** Skip empty rows */
    skipEmptyRows: z.boolean().default(true),
})
    .strict();
// ============================================================================
// Upload Inputs
// ============================================================================
/** JSON upload input */
export const JsonUploadInputSchema = z
    .object({
    format: z.literal("json"),
    /** Array of product data matching the vertical schema */
    products: z.array(z.record(z.string(), z.unknown())).min(1),
})
    .strict();
/** CSV upload input */
export const CsvUploadInputSchema = z
    .object({
    format: z.literal("csv"),
    /** Raw CSV content */
    content: z.string().min(1),
    /** Column mappings */
    mapping: z.array(ColumnMappingSchema).min(1),
    /** CSV parsing options */
    options: CsvOptionsSchema.optional(),
})
    .strict();
/** Form upload input (single product) */
export const FormUploadInputSchema = z
    .object({
    format: z.literal("form"),
    /** Single product data */
    product: z.record(z.string(), z.unknown()),
})
    .strict();
/** Union of all upload input types */
export const UploadInputSchema = z.discriminatedUnion("format", [
    JsonUploadInputSchema,
    CsvUploadInputSchema,
    FormUploadInputSchema,
]);
// ============================================================================
// Upload Errors
// ============================================================================
/** Upload validation error for a specific row/field */
export const UploadErrorSchema = z
    .object({
    /** Row number (1-based for user display, 0 for header errors) */
    row: z.number().int().min(0),
    /** Field/column that has the error */
    field: z.string().optional(),
    /** Error message */
    error: z.string().min(1),
    /** The invalid value */
    value: z.unknown().optional(),
    /** Error code for programmatic handling */
    code: z.string().optional(),
})
    .strict();
/** Default upload service configuration */
export const DEFAULT_UPLOAD_CONFIG = {
    maxRows: 10000,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    previewSize: 100,
    batchSize: 100,
};
